import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import {
  IUserConfiguration,
  IUserConfigurationResponse,
} from '../../../store/user-configuration/user-configuration.model';
import { Observable } from 'rxjs';
import { ResponseInterface } from '../../model/interface/generic-api-response.model';

interface BaseResponseInterface {
  success: boolean;
  data: {};
}

export interface SavePageFiltersAndSettingsResponseInterface extends BaseResponseInterface {}

export interface GetFilterInterface {
  id: string;
  key: string;
  value: string;
}

export interface GetPageFiltersAndSettingsResponseInterface extends BaseResponseInterface {
  data: GetFilterInterface[];
}

export interface FilterBodyInterface {
  page: string;
  filtersAndSettings: FiltersAndSettingsInterface[];
}

interface FiltersAndSettingsInterface {
  key: string;
  value: string;
}

interface FilterDataInterface {
  [key: string]: Object[];
}

export interface PreparedFilterDataInterface {
  [key: string]: number[] | string[];
}

@Injectable({
  providedIn: 'root',
})
export class PageFiltersAndSettingsService {
  constructor(public http: HttpClient, @Inject('API_BASE_URL') private readonly baseUrl: string) {}

  private filterUrls = {
    GET: {
      FILTER_URL: `${this.baseUrl}/user/user-filters-and-settings`,
    },
    POST: {
      FILTER_URL: `${this.baseUrl}/user/user-filters-and-settings`,
    },
  };

  private readonly routes = {
    accountSettings: `${this.baseUrl}/user/settings`,
  };

  savePageFiltersAndSettings(filterBody: FilterBodyInterface): Promise<SavePageFiltersAndSettingsResponseInterface> {
    return new Promise((resolve, reject) => {
      this.http.post(this.filterUrls.POST.FILTER_URL, filterBody).subscribe(
        (response: SavePageFiltersAndSettingsResponseInterface) => {
          if (response.success) {
            resolve(response);
          } else {
            reject();
          }
        },
        (error: HttpErrorResponse) => {
          reject(error);
        },
      );
    });
  }

  getPageFiltersAndSettings(page: string, userId: string): Promise<PreparedFilterDataInterface> {
    const baseHttpParams = new HttpParams().append('limit', '1000');
    const options = {
      params: baseHttpParams.append('s', JSON.stringify({ userId, page })),
    };

    return new Promise((resolve, reject) => {
      this.http.get(this.filterUrls.GET.FILTER_URL, { ...options }).subscribe(
        (response: GetPageFiltersAndSettingsResponseInterface) => {
          resolve(this.prepareFilterDataResponse(response));
        },
        (error: HttpErrorResponse) => {
          reject(error);
        },
      );
    });
  }

  prepareFilterDataResponse(response: GetPageFiltersAndSettingsResponseInterface): PreparedFilterDataInterface {
    const setting: PreparedFilterDataInterface = {};
    response.data.forEach((settings) => {
      setting[settings.key] = settings.value.split(',');
    });
    return setting;
  }

  prepareFilterData(filterData: FilterDataInterface, pageName: string): FilterBodyInterface {
    const objectKeys: string[] = Object.keys(filterData);

    const filterResponse: FilterBodyInterface = {
      page: pageName,
      filtersAndSettings: [],
    };

    objectKeys.forEach((keyName) => {
      try {
        if (Array.isArray(filterData[keyName])) {
          filterResponse.filtersAndSettings.push({
            key: keyName,
            value: encodeURI(filterData[keyName].map((x) => x).join(',')),
          });
        }
      } catch (e) {
        console.error(e);
      }
    });

    return filterResponse;
  }

  public getUserConfigurationData(): Observable<ResponseInterface<IUserConfigurationResponse>> {
    const params: HttpParams = new HttpParams().set('fields', 'configuration');
    return this.http.get<ResponseInterface<IUserConfigurationResponse>>(this.routes.accountSettings, { params });
  }

  public updateUserConfigurationData(
    configuration: IUserConfiguration,
  ): Observable<ResponseInterface<IUserConfigurationResponse>> {
    return this.http.patch<ResponseInterface<IUserConfigurationResponse>>(
      this.routes.accountSettings,
      {
        configuration,
      }
    );
  }

}
